import Image from "next/image";
import { useEntities } from "@/contexts/entities/EntitiesContext";
import { useEffect, useState, useRef, RefObject, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { Competency } from "@/lib/models";
import ArrowRight from "../icons/ArrowRight";
import {
  StyledButton,
  ButtonTypes,
  ButtonSizes,
  IconTypes,
} from "../StyledButton";
import Link from "next/link";

export const PopularCompetencies = () => {
  const popularCompetencyIds = [
    "FL.MST.4",
    "GC.IS.4",
    "OT.PS.2",
    "FK.SS.2",
    "LL.SD.3",
  ];

  const buttonRef1 = useRef<HTMLButtonElement>(null);
  const buttonRef2 = useRef<HTMLButtonElement>(null);
  const buttonRef3 = useRef<HTMLButtonElement>(null);
  const buttonRef4 = useRef<HTMLButtonElement>(null);
  const buttonRef5 = useRef<HTMLButtonElement>(null);

  const buttonRefs: RefObject<HTMLButtonElement | undefined>[] = useMemo(
    () => [buttonRef1, buttonRef2, buttonRef3, buttonRef4, buttonRef5],
    [],
  );

  const { getCompetency } = useEntities();

  const popularCompetencies = popularCompetencyIds.map((competencyId) => {
    return getCompetency(competencyId)!;
  });

  const [selectedCompetency, setSelectedCompetency] = useState<Competency>(
    popularCompetencies[0]!,
  );

  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);

  const { ref, inView } = useInView({ threshold: 0.3 });

  const [index, setIndex] = useState(0);
  const increment = () => setIndex((i) => i + 1);

  useEffect(() => {
    const id = setInterval(increment, 4000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!hasUserInteracted) {
      setSelectedCompetency(
        popularCompetencies[index % popularCompetencies.length],
      );
      if (inView && buttonRefs[index % popularCompetencies.length].current) {
        buttonRefs[index % popularCompetencies.length].current?.scrollIntoView({
          inline: "center",
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [index, popularCompetencies, hasUserInteracted, inView, buttonRefs]);

  const handleClick = (competency: Competency) => {
    setHasUserInteracted(true);
    setSelectedCompetency(competency);
  };

  return (
    <div
      id="page-content"
      className="mx-auto scroll-mt-[72px] text-center medium:scroll-mt-20"
    >
      <div className="mx-auto flex max-w-[548px] flex-col gap-y-8 px-4 text-center opacity-100 medium:max-w-[1156px] medium:gap-y-12">
        <h2 className="text-4xl leading-tight medium:mb-2 medium:text-[64px] medium:tracking-[-1px]">
          XQ Competencies
        </h2>
        <p className="font-roobert text-xl leading-none text-dark-color medium:text-2xl medium:font-medium">
          Educate and empower the whole student.
        </p>
        <div className="z-10">
          <p className="leading-5 text-gray-text medium:text-xl medium:leading-[26px]">
            The Competencies break down the critical elements of lifelong
            learning into teachable skills.
          </p>
          <p className="leading-5 text-gray-text medium:text-xl medium:leading-[26px]">
            Designed to be used with academic standards, they are effective in
            any state, school, and outside-of-school setting.
          </p>
        </div>

        <p className="z-10 font-roobert font-medium leading-none text-dark-color medium:text-xl">
          Explore Popular XQ Competencies
        </p>
      </div>

      <div className="-mb-[98px] -mt-[102px] max-w-[100vw] overflow-x-scroll medium:-mb-[90px] medium:-mt-[96px]">
        <div
          ref={ref}
          className="relative mx-auto h-[418px] w-[1004px] bg-[url('/assets/homepage/hexagon_background.png')] bg-cover bg-center  bg-no-repeat"
        >
          <div className="absolute left-[144px] top-[128px] flex flex-row gap-x-1">
            {popularCompetencies.map((competency, index) => {
              return (
                <PopularCompetency
                  competency={competency!}
                  selectedCompetency={selectedCompetency}
                  handleClick={() => handleClick(competency)}
                  key={competency!.id}
                  refProp={buttonRefs[index]}
                />
              );
            })}
          </div>
        </div>
      </div>

      <SelectedCompetencyDetails competency={selectedCompetency} />

      <StyledButton
        buttonType={ButtonTypes.Primary}
        size={ButtonSizes.Medium}
        iconType={IconTypes.Right}
        className="mt-8 w-fit medium:h-16 medium:pl-6 medium:pr-4"
        as="link"
        href="/"
      >
        Explore all XQ Competencies
        <ArrowRight fill="#FFFFFF" />
      </StyledButton>
    </div>
  );
};

type PopularCompetencyProps = {
  competency: Competency;
  selectedCompetency: Competency;
  handleClick: (competency: Competency) => void;
  refProp: RefObject<HTMLButtonElement | undefined>;
};

const PopularCompetency = ({
  competency,
  selectedCompetency,
  handleClick,
  refProp,
}: PopularCompetencyProps) => {
  const isSelected = competency.id == selectedCompetency.id;

  const buttonElement = document.getElementById(
    `popular-competency-${competency.id}`,
  );

  const onClick = () => {
    handleClick(competency);
    if (buttonElement) {
      buttonElement.scrollIntoView({
        inline: "center",
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  const selectedStyles =
    "opacity-100 scale-[1.1] drop-shadow-[0_15px_15px_rgba(0,0,0,0.20)] z-20";
  return (
    <button
      onClick={onClick}
      className="relative h-[162px] w-[140px]"
      id={`popular-competency-${competency.id}`}
      ref={refProp as RefObject<HTMLButtonElement>}
    >
      <Image
        src={competency!.namedIcon.sourceUrl!}
        width={140}
        height={162}
        alt={competency!.name}
        className={`absolute top-0 transition duration-300 ease-in-out hover:opacity-100 ${
          isSelected ? selectedStyles : "opacity-40"
        }`}
      />
    </button>
  );
};

type SelectedCompetencyDetailsProps = {
  competency: Competency;
};

const SelectedCompetencyDetails = ({
  competency,
}: SelectedCompetencyDetailsProps) => {
  const LOID = competency.id.slice(0, 2);

  return (
    <div className="flex flex-col gap-4 medium:gap-6">
      <h3
        className={`text-${LOID}-70 z-10 font-roobert text-2xl font-medium medium:text-[32px]`}
      >
        {competency.name}
      </h3>
      <p
        className={`text-${LOID}-70 font-roobert text-base font-normal leading-tight medium:text-xl`}
      >
        {competency.descriptionShort}
      </p>
      <Link
        shallow={true}
        href={{
          pathname: "/",
          query: { selection: competency.id },
        }}
        className={`text-${LOID}-70 font-roobert text-base font-semibold underline medium:text-xl`}
      >
        Learn more
      </Link>
    </div>
  );
};
